/**
 * SettingsPanel — context-sensitive inspector for the selected layer.
 * Controls vary by layer.type. Range sliders use the cheap updateLayer/
 * updateAdjust path on input and commit history once on release (patch* on
 * change/mouseUp); discrete edits (selects, colors, blur) use patch* directly.
 */
import React from 'react';
import { ImageUp, Library, FlipHorizontal2, FlipVertical2 } from 'lucide-react';
import { T } from '../../../components/em/tokens';
import { BLEND_MODES, FONT_FAMILIES, FONT_WEIGHTS } from '../pgConstants';

// ── local style atoms ──────────────────────────────────────────────────────
const sectionTitle = {
  font: `600 11px/1 ${T.font}`,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
  color: T.fg7,
  margin: '0 0 10px',
};

const labelStyle = { font: `500 12px/1.3 ${T.font}`, color: T.fg3 };

const numStyle = {
  width: 64, padding: '6px 8px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rSm,
  background: T.bgCard, color: T.fg1, font: `500 12px/1 ${T.font}`,
  outline: 'none',
};

const selectStyle = {
  width: '100%', padding: '7px 10px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rSm,
  background: T.bgCard, color: T.fg1, font: `500 12px/1 ${T.font}`,
  outline: 'none', cursor: 'pointer',
};

const textInputStyle = {
  width: '100%', padding: '7px 10px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rSm,
  background: T.bgCard, color: T.fg1, font: `400 13px/1.3 ${T.font}`,
  outline: 'none',
};

function Section({ title, children }) {
  return (
    <div style={{ padding: '14px 14px', borderBottom: `1px solid ${T.divider}` }}>
      <div style={sectionTitle}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>{children}</div>
    </div>
  );
}

const ColorInput = ({ value, onChange }) => (
  <input
    type="color"
    value={value || '#000000'}
    onChange={onChange}
    style={{ width: 36, height: 28, padding: 0, border: `1px solid ${T.border}`, borderRadius: T.rSm, background: T.bgCard, cursor: 'pointer' }}
  />
);

function NumberInput({ value, onChange, min, max, step = 1 }) {
  return (
    <input
      type="number"
      value={value ?? 0}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
      style={numStyle}
    />
  );
}

// Range row: live-updates while sliding (onUpdate), commits to history once on
// release/change (onCommit). Shows the numeric value on the right.
function RangeRow({ label, value, min, max, step = 1, onUpdate, onCommit, suffix }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={labelStyle}>{label}</span>
        <span style={{ font: `500 11px/1 ${T.font}`, color: T.fg5 }}>
          {value}{suffix || ''}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onUpdate(Number(e.target.value))}
        onMouseUp={(e) => onCommit(Number(e.target.value))}
        onTouchEnd={(e) => onCommit(Number(e.target.value))}
        style={{ width: '100%', accentColor: 'var(--em-orange)' }}
      />
    </div>
  );
}

function ToggleBtn({ label, active, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 10px', borderRadius: T.rSm,
        border: `1px solid ${active ? T.orange : T.border}`,
        background: active ? T.orange50 : 'transparent',
        color: active ? T.orange : T.fg3,
        cursor: 'pointer', font: `500 12px/1 ${T.font}`,
      }}
    >
      {Icon && <Icon size={14} />}{label}
    </button>
  );
}

const adj = (layer) => layer.adjustments || {};

export default function SettingsPanel({ state, actions, onOpenAssets, onReplaceImage }) {
  const selected = state.layers.find((l) => l.id === state.selectedLayerId);

  if (!selected) {
    return (
      <div className="pg-settings-panel" style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ margin: 0, color: T.fg7, font: `400 13px/1.5 ${T.font}` }}>
          Select a layer to edit
        </p>
      </div>
    );
  }

  const id = selected.id;
  const a = adj(selected);
  const isFillType = selected.type === 'background' || selected.type === 'foreground';

  return (
    <div className="pg-settings-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflowY: 'auto' }}>
      {/* Transform — all types */}
      <Section title="Transform">
        <RangeRow
          label="Opacity"
          value={selected.opacity ?? 1}
          min={0}
          max={1}
          step={0.05}
          onUpdate={(v) => actions.updateLayer(id, { opacity: v })}
          onCommit={(v) => actions.patchLayer(id, { opacity: v })}
        />
        <Row label="Rotation">
          <NumberInput value={Math.round(selected.rotation ?? 0)} step={1} onChange={(v) => actions.patchLayer(id, { rotation: v })} />
          <span style={{ font: `500 11px/1 ${T.font}`, color: T.fg7 }}>deg</span>
        </Row>
        <Row label="Position X / Y">
          <NumberInput value={Math.round(selected.x ?? 0)} onChange={(v) => actions.patchLayer(id, { x: v })} />
          <NumberInput value={Math.round(selected.y ?? 0)} onChange={(v) => actions.patchLayer(id, { y: v })} />
        </Row>
        <Row label="Size W / H">
          <NumberInput value={Math.round(selected.width ?? 0)} min={1} onChange={(v) => actions.patchLayer(id, { width: Math.max(1, v) })} />
          <NumberInput value={Math.round(selected.height ?? 0)} min={1} onChange={(v) => actions.patchLayer(id, { height: Math.max(1, v) })} />
        </Row>
        <Row label="Locked">
          <ToggleBtn label={selected.locked ? 'Locked' : 'Unlocked'} active={!!selected.locked} onClick={() => actions.toggleLock(id)} />
        </Row>
      </Section>

      {/* Fill — background & foreground */}
      {isFillType && (
        <Section title="Fill">
          <Row label="Type">
            <select
              value={selected.fillType || 'solid'}
              onChange={(e) => actions.patchLayer(id, { fillType: e.target.value })}
              style={{ ...selectStyle, width: 120 }}
            >
              <option value="solid">Solid</option>
              <option value="gradient">Gradient</option>
            </select>
          </Row>

          {(selected.fillType || 'solid') === 'solid' ? (
            <Row label="Color">
              <ColorInput value={selected.fill} onChange={(e) => actions.patchLayer(id, { fill: e.target.value })} />
            </Row>
          ) : (
            <>
              <Row label="From">
                <ColorInput value={selected.gradientFrom} onChange={(e) => actions.patchLayer(id, { gradientFrom: e.target.value })} />
              </Row>
              <Row label="To">
                <ColorInput value={selected.gradientTo} onChange={(e) => actions.patchLayer(id, { gradientTo: e.target.value })} />
              </Row>
              <Row label="Angle">
                <NumberInput value={Math.round(selected.gradientAngle ?? 0)} min={0} max={360} onChange={(v) => actions.patchLayer(id, { gradientAngle: Math.max(0, Math.min(360, v)) })} />
                <span style={{ font: `500 11px/1 ${T.font}`, color: T.fg7 }}>deg</span>
              </Row>
            </>
          )}
        </Section>
      )}

      {/* Image */}
      {selected.type === 'image' && (
        <>
          <Section title="Source">
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onReplaceImage}
                style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: T.rSm, border: `1px solid ${T.border}`, background: 'transparent', color: T.fg2, cursor: 'pointer', font: `500 12px/1 ${T.font}` }}
              >
                <ImageUp size={14} />Replace image
              </button>
              <button
                type="button"
                onClick={onOpenAssets}
                style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 10px', borderRadius: T.rSm, border: `1px solid ${T.border}`, background: 'transparent', color: T.fg2, cursor: 'pointer', font: `500 12px/1 ${T.font}` }}
              >
                <Library size={14} />From library
              </button>
            </div>
          </Section>

          <Section title="Adjustments">
            <RangeRow label="Brightness" value={a.brightness ?? 100} min={0} max={200} onUpdate={(v) => actions.updateAdjust(id, { brightness: v })} onCommit={(v) => actions.patchAdjust(id, { brightness: v })} suffix="%" />
            <RangeRow label="Contrast" value={a.contrast ?? 100} min={0} max={200} onUpdate={(v) => actions.updateAdjust(id, { contrast: v })} onCommit={(v) => actions.patchAdjust(id, { contrast: v })} suffix="%" />
            <RangeRow label="Saturation" value={a.saturation ?? 100} min={0} max={200} onUpdate={(v) => actions.updateAdjust(id, { saturation: v })} onCommit={(v) => actions.patchAdjust(id, { saturation: v })} suffix="%" />
            <RangeRow label="Blur" value={a.blur ?? 0} min={0} max={20} onUpdate={(v) => actions.updateAdjust(id, { blur: v })} onCommit={(v) => actions.patchAdjust(id, { blur: v })} suffix="px" />
            <Row label="Flip">
              <ToggleBtn label="H" icon={FlipHorizontal2} active={!!a.flipH} onClick={() => actions.patchAdjust(id, { flipH: !a.flipH })} />
              <ToggleBtn label="V" icon={FlipVertical2} active={!!a.flipV} onClick={() => actions.patchAdjust(id, { flipV: !a.flipV })} />
            </Row>
            <Row label="Blend mode">
              <select
                value={a.blendMode || 'source-over'}
                onChange={(e) => actions.patchAdjust(id, { blendMode: e.target.value })}
                style={{ ...selectStyle, width: 140 }}
              >
                {BLEND_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Row>
          </Section>

          <Section title="Drop shadow">
            <Row label="Enabled">
              <input
                type="checkbox"
                checked={!!a.shadowEnabled}
                onChange={(e) => actions.patchAdjust(id, { shadowEnabled: e.target.checked })}
                style={{ width: 16, height: 16, accentColor: 'var(--em-orange)', cursor: 'pointer' }}
              />
            </Row>
            {a.shadowEnabled && (
              <>
                <Row label="Color">
                  <ColorInput value={(a.shadowColor || '#000000').slice(0, 7)} onChange={(e) => actions.patchAdjust(id, { shadowColor: e.target.value })} />
                </Row>
                <RangeRow label="Blur" value={a.shadowBlur ?? 12} min={0} max={40} onUpdate={(v) => actions.updateAdjust(id, { shadowBlur: v })} onCommit={(v) => actions.patchAdjust(id, { shadowBlur: v })} suffix="px" />
                <RangeRow label="Offset X" value={a.shadowOffsetX ?? 0} min={-40} max={40} onUpdate={(v) => actions.updateAdjust(id, { shadowOffsetX: v })} onCommit={(v) => actions.patchAdjust(id, { shadowOffsetX: v })} suffix="px" />
                <RangeRow label="Offset Y" value={a.shadowOffsetY ?? 6} min={-40} max={40} onUpdate={(v) => actions.updateAdjust(id, { shadowOffsetY: v })} onCommit={(v) => actions.patchAdjust(id, { shadowOffsetY: v })} suffix="px" />
              </>
            )}
          </Section>
        </>
      )}

      {/* Name (text) */}
      {selected.type === 'name' && (
        <Section title="Text">
          <input
            type="text"
            value={selected.text ?? ''}
            placeholder="Full Name"
            onChange={(e) => actions.updateLayer(id, { text: e.target.value })}
            onBlur={(e) => actions.patchLayer(id, { text: e.target.value })}
            style={textInputStyle}
          />
          <Row label="Font">
            <select
              value={selected.fontFamily || FONT_FAMILIES[0]}
              onChange={(e) => actions.patchLayer(id, { fontFamily: e.target.value })}
              style={{ ...selectStyle, width: 150 }}
            >
              {FONT_FAMILIES.map((f) => (
                <option key={f} value={f} style={{ fontFamily: f }}>
                  {f.replace(/['"]/g, '').split(',')[0]}
                </option>
              ))}
            </select>
          </Row>
          <Row label="Size">
            <NumberInput value={Math.round(selected.fontSize ?? 24)} min={4} onChange={(v) => actions.patchLayer(id, { fontSize: Math.max(4, v) })} />
            <span style={{ font: `500 11px/1 ${T.font}`, color: T.fg7 }}>px</span>
          </Row>
          <Row label="Weight">
            <select
              value={selected.fontWeight || '600'}
              onChange={(e) => actions.patchLayer(id, { fontWeight: e.target.value })}
              style={{ ...selectStyle, width: 120 }}
            >
              {FONT_WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </Row>
          <Row label="Color">
            <ColorInput value={selected.fontColor} onChange={(e) => actions.patchLayer(id, { fontColor: e.target.value })} />
          </Row>
          <Row label="Align">
            <select
              value={selected.textAlign || 'center'}
              onChange={(e) => actions.patchLayer(id, { textAlign: e.target.value })}
              style={{ ...selectStyle, width: 120 }}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </Row>
        </Section>
      )}
    </div>
  );
}
